/* eslint-disable react/react-in-jsx-scope */
import { useCallback, useEffect, useState } from "react";

import { Form, Input, InputNumber, Button, Table, Select, Space } from "antd";

import { Link } from "react-router-dom";

import PropTypes from "prop-types";

import { useTranslation } from "react-i18next"; // Add i18n import
import apiClient from "../../helpers/apiClient";

import customizeRequiredMark from "../../components/custom_antd/CustomizeRequiredMark";

import TopBack from "../reservations/components/subcompdash/Topback";
import { useToast } from "../../contexts/ToastContext";
import confirmModal from "../../components/custom_antd/confirmModal";

const { TextArea } = Input;

const AddonDetails = ({ match }) => {
    const { t } = useTranslation(["addons"]); // Add translation hook
    const toast = useToast();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [carOptions, setCarOptions] = useState([]);
    const [selectedCars, setSelectedCars] = useState([]);
    const [editMode, setEditMode] = useState(false);

    const { id } = match.params;

    const fetchAllCars = useCallback(async () => {
        try {
            const res = await apiClient.get("/api/car/list");
            const options = res?.data?.data?.result?.map((car) => ({
                value: car.id,
                label: `${car.carName} | ${car.carNumber}`,
            }));
            setCarOptions(options);
        } catch (error) {
            // console.error(error);
            toast.error({ message: t("_addonDetails.failedToFetchCars") });
        }
    }, [t, toast]);

    const fetchData = useCallback(async () => {
        try {
            const res = await apiClient.get(`/api/addon/${id}`);
            setData(res.data.data);
            form.setFieldsValue({
                name: res.data.data.name,
                desc: res.data.data.desc,
                rate: res.data.data.rate,
                quantity: res.data.data.quantity,
            });
            setSelectedCars(res.data.data.cars?.map((car) => car) || []);
        } catch (_error) {
            // console.error(error);
            toast.error({ message: t("_addonDetails.failedToFetchAddonDetails") });
        }
    }, [form, id, t, toast]);

    useEffect(() => {
        fetchData();
        fetchAllCars();
    }, [id, fetchData, fetchAllCars]);

    const handleSubmit = async (values) => {
        try {
            setLoading(true);
            const payload = {
                name: values.name,
                desc: values.desc,
                rate: Number(values.rate),
                quantity: Number(values.quantity),
                cars: selectedCars,
            };

            const resp = await apiClient.patch(`/api/addon/${id}`, payload);
            if (resp?.data?.success) {
                toast.success({ message: t("addonUpdatedSuccess") });
                setEditMode(false);
                fetchData();
            }
        } catch (error) {
            if (error.response?.status === 401) {
                toast.error({ message: t("_addonDetails.notAuthorizedToEdit") });
            } else {
                toast.error({ message: t("_addonDetails.failedToUpdate") });
            }
            // console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        const confirmed = await confirmModal({
            title: t("_addonDetails.confirmDelete"),
            okText: t("_addonDetails.delete"),
            cancelText: t("cancel"),
            danger: true,
        });

        if (confirmed) {
            try {
                const resp = await apiClient.delete(`/api/addon/${id}`);
                if (resp?.data?.success) {
                    toast.success({ message: t("addonDeletedSuccess") });
                    window.location.href = "/add-ons";
                }
            } catch (error) {
                if (error.response?.status === 401) {
                    toast.error({ message: t("_addonDetails.notAuthorizedToDelete") });
                } else {
                    toast.error({ message: t("_addonDetails.failedToDelete") });
                }
                // console.error(error);
            }
        }
    };

    const columns = [
        {
            title: t("id"),
            dataIndex: "id",
            width: "15rem",
        },
        {
            title: t("_addonDetails.carName"),
            dataIndex: "carName",
            width: "15rem",
        },
        {
            title: t("_addonDetails.vin"),
            dataIndex: "VIN",
        },
        {
            title: t("_addonDetails.plateNo"),
            dataIndex: "carNumber",
        },
        {
            title: t("_addonDetails.action"),
            width: "8rem",
            render: (_, record) => (
                <Space>
                    <Link to={`/cardetails/${record.VIN}`}>
                        <Button type="link">{t("view")}</Button>
                    </Link>
                    {editMode && (
                        <Button
                            type="link"
                            danger
                            onClick={() => {
                                setSelectedCars((prev) => prev.filter((_id) => _id !== record.id));
                                setData((prev) => ({
                                    ...prev,
                                    cars: prev.cars.filter((car) => car.id !== record.id),
                                }));
                            }}>
                            {t("_addonDetails.remove")}
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <>
            <TopBack />
            <div className="p-6 rounded-2xl mt-2 min-h-0 flex-1 bg-white">
                <div className="flex flex-col h-full">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-lg font-bold text-[#5d5dff]">{t("addonDetails")}</h2>
                        <Space>
                            {editMode ? (
                                <>
                                    <Button type="primary" loading={loading} onClick={() => form.submit()}>
                                        {t("saveChanges")}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setEditMode(false);
                                            form.setFieldsValue({
                                                name: data?.name,
                                                desc: data?.desc,
                                                rate: data?.rate,
                                                quantity: data?.quantity,
                                            });
                                            setSelectedCars(data?.cars?.map((car) => car.id) || []);
                                        }}>
                                        {t("cancel")}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button danger onClick={handleDelete}>
                                        {t("_addonDetails.delete")}
                                    </Button>
                                    <Button type="primary" onClick={() => setEditMode(true)}>
                                        {t("editAddon")}
                                    </Button>
                                </>
                            )}
                        </Space>
                    </div>

                    <div className="bg-white p-8 rounded-xl mb-6">
                        {editMode ? (
                            <Form
                                form={form}
                                layout="vertical"
                                onFinish={handleSubmit}
                                initialValues={{
                                    name: data?.name,
                                    desc: data?.desc,
                                    rate: data?.rate,
                                    quantity: data?.quantity,
                                }}
                                requiredMark={customizeRequiredMark}>
                                <Form.Item
                                    label={t("name")}
                                    name="name"
                                    rules={[
                                        {
                                            required: true,
                                            message: t("_addonDetails.nameRequired"),
                                        },
                                    ]}>
                                    <Input />
                                </Form.Item>

                                <Form.Item
                                    label={t("rate")}
                                    name="rate"
                                    rules={[
                                        {
                                            required: true,
                                            message: t("_addonDetails.rateRequired"),
                                        },
                                        {
                                            type: "number",
                                            min: 0,
                                            message: t("_addonDetails.ratePositive"),
                                        },
                                    ]}>
                                    <InputNumber style={{ width: "100%" }} min={0} />
                                </Form.Item>

                                <Form.Item
                                    label={t("quantity")}
                                    name="quantity"
                                    rules={[
                                        {
                                            required: true,
                                            message: t("_addonDetails.quantityRequired"),
                                        },
                                        {
                                            type: "number",
                                            min: 0,
                                            message: t("_addonDetails.quantityPositive"),
                                        },
                                    ]}>
                                    <InputNumber style={{ width: "100%" }} min={0} />
                                </Form.Item>

                                <Form.Item
                                    label={t("description")}
                                    name="desc"
                                    rules={[
                                        {
                                            required: true,
                                            message: t("_addonDetails.descriptionRequired"),
                                        },
                                    ]}>
                                    <TextArea rows={4} />
                                </Form.Item>
                            </Form>
                        ) : (
                            <div className="space-y-6">
                                <div className="flex flex-row gap-2">
                                    <div className="text-gray-500 mb-1">{t("name")}:</div>
                                    <div className="text-lg">{data?.name}</div>
                                </div>
                                <div className="flex flex-row gap-2">
                                    <div className="text-gray-500 mb-1">{t("rate")}:</div>
                                    <div className="text-lg">{data?.rate}</div>
                                </div>
                                <div className="flex flex-row gap-2">
                                    <div className="text-gray-500 mb-1">{t("quantity")}:</div>
                                    <div className="text-lg">{data?.quantity}</div>
                                </div>
                                <div className="flex flex-row gap-2">
                                    <div className="text-gray-500 mb-1">{t("description")}:</div>
                                    <div className="text-lg whitespace-pre-wrap">{data?.desc}</div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="mb-4">
                        <h3 className="text-lg font-bold text-[#5d5dff]">
                            {t("_addonDetails.attachedCars")} ({data?.cars?.length || 0})
                        </h3>
                    </div>

                    <div className="h-[45rem] overflow-y-auto">
                        {editMode && (
                            <Select
                                mode="multiple"
                                style={{ width: "100%", marginBottom: "1rem" }}
                                placeholder={t("_addonDetails.selectCarsPlaceholder")}
                                value={selectedCars}
                                onChange={setSelectedCars}
                                options={carOptions}
                                filterOption={(input, option) =>
                                    option?.label.toLowerCase().includes(input.toLowerCase())
                                }
                                optionFilterProp="label"
                            />
                        )}

                        <Table className="sc-table-frame" columns={columns} dataSource={data?.cars || []} rowKey="id" pagination={false} />
                    </div>
                </div>
            </div>
        </>
    );
};

AddonDetails.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            id: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
};

export default AddonDetails;
